import type {
  ExperimentFormData,
  FeedbackFlow,
  FeedbackFlowContent,
  FeedbackFlowQuestion,
  FeedbackFlowQuestionAnswer,
  FeedbackFlowQuestionInputType,
  FeedbackFlowQuestionSection,
  TaggedTarget,
} from "../types/experiment.types";

export const DEFAULT_INTRO_DESCRIPTION =
  "Welcome to our research study.\n\nWe're exploring how users experience this process. Your feedback will help us improve the journey.\n\nYou'll walk through a simulated flow. Please interact naturally and share your honest impressions.\n\nEstimated time: 3-5 minutes.";

export const DEFAULT_OUTRO_DESCRIPTION =
  "Thank you for participating in our study. Your feedback is incredibly valuable to us.";

const DRAFT_QUESTION_ID_PREFIX = "draft-";

export const FEEDBACK_FLOW_SECTION_ORDER: FeedbackFlowQuestionSection[] = [
  "demographics",
  "personal_info",
  "prototype",
];

const FEEDBACK_FLOW_SECTION_ORDER_INDEX = new Map(
  FEEDBACK_FLOW_SECTION_ORDER.map((section, index) => [section, index]),
);

export const LIKERT_DEFAULT_OPTIONS = [
  "Strongly Disagree",
  "Disagree",
  "Neutral",
  "Agree",
  "Strongly Agree",
];

type LegacyQuestion = {
  id?: string;
  question?: string;
  inputType?: FeedbackFlowQuestionInputType;
  options?: string[];
};

type LegacyAnnotation = LegacyQuestion & {
  elementSelector?: string;
  positionX?: number;
  positionY?: number;
  width?: number;
  height?: number;
  createdAtScrollX?: number;
  createdAtScrollY?: number;
  pageRoute?: string;
};

type LegacyFrontMatter = {
  enabled?: boolean;
  welcomeMessage?: string;
  disclaimer?: string;
};

type LegacyEndMatter = {
  enabled?: boolean;
  thankYouMessage?: string;
  terms?: string;
};

type FeedbackFlowHydrationSource = ExperimentFormData &
  Partial<{
    annotations: LegacyAnnotation[];
    frontMatter: LegacyFrontMatter;
    endMatter: LegacyEndMatter;
    demographicsQuestions: LegacyQuestion[];
    personalInfoQuestions: LegacyQuestion[];
  }>;

export const createEmptyFeedbackContent = (
  overrides?: Partial<FeedbackFlowContent>,
): FeedbackFlowContent => ({
  enabled: false,
  description: "",
  terms: "",
  ...overrides,
});

export const createEmptyFeedbackFlow = (): FeedbackFlow => ({
  intro: createEmptyFeedbackContent({ description: DEFAULT_INTRO_DESCRIPTION }),
  outro: createEmptyFeedbackContent({ description: DEFAULT_OUTRO_DESCRIPTION }),
  demographicsEnabled: false,
  personalInfoEnabled: false,
  questions: [],
});

export const createDraftFeedbackFlowQuestionId = () =>
  `${DRAFT_QUESTION_ID_PREFIX}${crypto.randomUUID()}`;

export const isDraftFeedbackQuestionId = (questionId: string): boolean =>
  questionId.startsWith(DRAFT_QUESTION_ID_PREFIX);

export const normalizeFeedbackFlowContent = (
  content?: Partial<FeedbackFlowContent> | null,
  fallbackDescription = "",
): FeedbackFlowContent =>
  createEmptyFeedbackContent({
    enabled: Boolean(content?.enabled),
    description: content?.description ?? fallbackDescription,
    terms: content?.terms ?? "",
  });

export const normalizeTaggedTarget = (
  taggedTarget?: TaggedTarget | null,
): TaggedTarget | null => {
  if (!taggedTarget) {
    return null;
  }

  const route = taggedTarget.route?.trim();
  const selector = taggedTarget.selector?.trim();
  const coordinates = taggedTarget.coordinates
    ? {
        x: taggedTarget.coordinates.x,
        y: taggedTarget.coordinates.y,
        width: taggedTarget.coordinates.width,
        height: taggedTarget.coordinates.height,
        createdAtScrollX: taggedTarget.coordinates.createdAtScrollX ?? 0,
        createdAtScrollY: taggedTarget.coordinates.createdAtScrollY ?? 0,
      }
    : undefined;

  if (!route && !selector && !coordinates) {
    return null;
  }

  return {
    ...(route ? { route } : {}),
    ...(selector ? { selector } : {}),
    ...(coordinates ? { coordinates } : {}),
  };
};

export const normalizeFeedbackFlowQuestion = (
  question: FeedbackFlowQuestion,
): FeedbackFlowQuestion => ({
  ...question,
  source: question.source ?? "general",
  premiseId: question.premiseId?.trim() || null,
  question: question.question ?? "",
  inputType: question.inputType ?? "text",
  options: normalizeOptions(question.inputType ?? "text", question.options),
  taggedTarget: normalizeTaggedTarget(question.taggedTarget),
  sortOrder:
    typeof question.sortOrder === "number" && Number.isFinite(question.sortOrder)
      ? question.sortOrder
      : 0,
  sentiment: question.sentiment ?? null,
  dislikeReason: question.dislikeReason ?? null,
});

export const createFeedbackFlowQuestion = (
  section: FeedbackFlowQuestionSection,
  overrides?: Partial<FeedbackFlowQuestion>,
): FeedbackFlowQuestion => ({
  id: createDraftFeedbackFlowQuestionId(),
  section,
  source: "general",
  premiseId: null,
  question: "",
  inputType: "text",
  options: [],
  taggedTarget: null,
  sortOrder: 0,
  sentiment: null,
  dislikeReason: null,
  ...overrides,
});

export const createDefaultDemographicsQuestions = (): FeedbackFlowQuestion[] => [
  createFeedbackFlowQuestion("demographics", {
    question: "What is your age range?",
    inputType: "multiple_choice",
    options: ["18-24", "25-34", "35-44", "45+"],
    sortOrder: 0,
  }),
  createFeedbackFlowQuestion("demographics", {
    question: "What industry do you work in?",
    inputType: "text",
    options: [],
    sortOrder: 1,
  }),
];

export const createDefaultPersonalInfoQuestions = (): FeedbackFlowQuestion[] => [
  createFeedbackFlowQuestion("personal_info", {
    question: "First Name",
    sortOrder: 0,
  }),
  createFeedbackFlowQuestion("personal_info", {
    question: "Last Name",
    sortOrder: 1,
  }),
  createFeedbackFlowQuestion("personal_info", {
    question: "Email Address",
    sortOrder: 2,
  }),
  createFeedbackFlowQuestion("personal_info", {
    question: "How did you find this experiment?",
    inputType: "multiple_choice",
    options: ["Social media", "Search engine", "Friend or colleague", "Other"],
    sortOrder: 3,
  }),
];

const normalizeOptions = (
  inputType: FeedbackFlowQuestionInputType,
  options?: string[],
): string[] => {
  const cleaned = (options ?? []).map((option) => option.trim()).filter(Boolean);
  if (inputType === "likert_scale") {
    return cleaned.length > 0 ? cleaned : [...LIKERT_DEFAULT_OPTIONS];
  }
  return cleaned;
};

const legacyQuestionToFeedbackQuestion = (
  section: FeedbackFlowQuestionSection,
  question: LegacyQuestion,
  sortOrder: number,
): FeedbackFlowQuestion =>
  createFeedbackFlowQuestion(section, {
    id: question.id ?? crypto.randomUUID(),
    question: question.question ?? "",
    inputType: question.inputType ?? "text",
    options: normalizeOptions(question.inputType ?? "text", question.options),
    sortOrder,
  });

const legacyAnnotationToFeedbackQuestion = (
  annotation: LegacyAnnotation,
  sortOrder: number,
): FeedbackFlowQuestion => {
  const taggedTarget: TaggedTarget | null =
    annotation.elementSelector ||
    annotation.width ||
    annotation.height ||
    annotation.positionX ||
    annotation.positionY
      ? {
          route: annotation.pageRoute ?? "/",
          selector: annotation.elementSelector,
          coordinates: {
            x: annotation.positionX ?? 0,
            y: annotation.positionY ?? 0,
            width: annotation.width ?? 0,
            height: annotation.height ?? 0,
            createdAtScrollX: annotation.createdAtScrollX ?? 0,
            createdAtScrollY: annotation.createdAtScrollY ?? 0,
          },
        }
      : null;

  return createFeedbackFlowQuestion("prototype", {
    id: annotation.id ?? crypto.randomUUID(),
    question: annotation.question ?? "",
    inputType: annotation.inputType ?? "text",
    options: normalizeOptions(annotation.inputType ?? "text", annotation.options),
    taggedTarget,
    sortOrder,
  });
};

const ensureQuestionSortOrders = (
  questions: FeedbackFlowQuestion[],
): FeedbackFlowQuestion[] => {
  const sectionCounters = new Map<FeedbackFlowQuestionSection, number>();

  return questions.map((question) => {
    const nextFallbackSortOrder = sectionCounters.get(question.section) ?? 0;
    sectionCounters.set(question.section, nextFallbackSortOrder + 1);

    if (
      typeof question.sortOrder === "number" &&
      Number.isFinite(question.sortOrder)
    ) {
      return normalizeFeedbackFlowQuestion(question);
    }

    return normalizeFeedbackFlowQuestion({
      ...question,
      sortOrder: nextFallbackSortOrder,
    });
  });
};

export const sortFeedbackFlowQuestions = (
  questions: FeedbackFlowQuestion[],
): FeedbackFlowQuestion[] =>
  ensureQuestionSortOrders(questions)
    .map((question, index) => ({ question, index }))
    .sort((left, right) => {
      const sectionDelta =
        (FEEDBACK_FLOW_SECTION_ORDER_INDEX.get(left.question.section) ?? 0) -
        (FEEDBACK_FLOW_SECTION_ORDER_INDEX.get(right.question.section) ?? 0);

      if (sectionDelta !== 0) {
        return sectionDelta;
      }

      const sortDelta = left.question.sortOrder - right.question.sortOrder;
      if (sortDelta !== 0) {
        return sortDelta;
      }

      return left.index - right.index;
    })
    .map(({ question }) => question);

export const hydrateFeedbackFlow = (
  experimentData: FeedbackFlowHydrationSource,
): FeedbackFlow => {
  if (experimentData.feedbackFlow) {
    const hydratedQuestions = sortFeedbackFlowQuestions(
      experimentData.feedbackFlow.questions ?? [],
    );

    return {
      intro: normalizeFeedbackFlowContent(
        experimentData.feedbackFlow.intro,
        DEFAULT_INTRO_DESCRIPTION,
      ),
      outro: normalizeFeedbackFlowContent(
        experimentData.feedbackFlow.outro,
        DEFAULT_OUTRO_DESCRIPTION,
      ),
      demographicsEnabled:
        experimentData.feedbackFlow.demographicsEnabled ??
        hydratedQuestions.some((question) => question.section === "demographics"),
      personalInfoEnabled:
        experimentData.feedbackFlow.personalInfoEnabled ??
        hydratedQuestions.some((question) => question.section === "personal_info"),
      questions: hydratedQuestions,
    };
  }

  const intro = normalizeFeedbackFlowContent(
    {
      enabled: Boolean(experimentData.frontMatter?.enabled),
      description: experimentData.frontMatter?.welcomeMessage,
      terms: experimentData.frontMatter?.disclaimer ?? "",
    },
    DEFAULT_INTRO_DESCRIPTION,
  );
  const outro = normalizeFeedbackFlowContent(
    {
      enabled: Boolean(experimentData.endMatter?.enabled),
      description: experimentData.endMatter?.thankYouMessage,
      terms: experimentData.endMatter?.terms ?? "",
    },
    DEFAULT_OUTRO_DESCRIPTION,
  );

  const demographicsQuestions = (experimentData.demographicsQuestions ?? []).map(
    (question, index) =>
      legacyQuestionToFeedbackQuestion("demographics", question, index),
  );
  const personalInfoQuestions = (experimentData.personalInfoQuestions ?? []).map(
    (question, index) =>
      legacyQuestionToFeedbackQuestion("personal_info", question, index),
  );
  const prototypeQuestions = (experimentData.annotations ?? []).map(
    (annotation, index) => legacyAnnotationToFeedbackQuestion(annotation, index),
  );

  return {
    intro,
    outro,
    demographicsEnabled: demographicsQuestions.length > 0,
    personalInfoEnabled: personalInfoQuestions.length > 0,
    questions: sortFeedbackFlowQuestions([
      ...demographicsQuestions,
      ...personalInfoQuestions,
      ...prototypeQuestions,
    ]),
  };
};

export const getQuestionsBySection = (
  feedbackFlow: FeedbackFlow,
  section: FeedbackFlowQuestionSection,
): FeedbackFlowQuestion[] =>
  sortFeedbackFlowQuestions(
    feedbackFlow.questions.filter((question) => question.section === section),
  );

export const getNextSortOrderForSection = (
  feedbackFlow: FeedbackFlow,
  section: FeedbackFlowQuestionSection,
): number => {
  const sectionQuestions = getQuestionsBySection(feedbackFlow, section);
  if (sectionQuestions.length === 0) {
    return 0;
  }

  return (
    Math.max(...sectionQuestions.map((question) => question.sortOrder ?? 0)) + 1
  );
};

export const getFeedbackFlowSectionEnabled = (
  feedbackFlow: FeedbackFlow,
  section: Extract<FeedbackFlowQuestionSection, "demographics" | "personal_info">,
): boolean =>
  section === "demographics"
    ? feedbackFlow.demographicsEnabled
    : feedbackFlow.personalInfoEnabled;

export const setFeedbackFlowSectionEnabled = (
  feedbackFlow: FeedbackFlow,
  section: Extract<FeedbackFlowQuestionSection, "demographics" | "personal_info">,
  enabled: boolean,
): FeedbackFlow => ({
  ...feedbackFlow,
  demographicsEnabled:
    section === "demographics" ? enabled : feedbackFlow.demographicsEnabled,
  personalInfoEnabled:
    section === "personal_info" ? enabled : feedbackFlow.personalInfoEnabled,
});

export const isTaggedFeedbackQuestion = (
  question: FeedbackFlowQuestion,
): boolean =>
  Boolean(
    question.taggedTarget?.selector ||
      question.taggedTarget?.coordinates,
  );

export const upsertFeedbackFlowQuestion = (
  feedbackFlow: FeedbackFlow,
  nextQuestion: FeedbackFlowQuestion,
): FeedbackFlow => {
  const normalizedNextQuestion = normalizeFeedbackFlowQuestion(nextQuestion);
  const existingQuestionIndex = feedbackFlow.questions.findIndex(
    (question) => question.id === normalizedNextQuestion.id,
  );

  if (existingQuestionIndex < 0) {
    return {
      ...feedbackFlow,
      questions: sortFeedbackFlowQuestions([
        ...feedbackFlow.questions,
        normalizedNextQuestion,
      ]),
    };
  }

  return {
    ...feedbackFlow,
    questions: sortFeedbackFlowQuestions(
      feedbackFlow.questions.map((question) =>
        question.id === normalizedNextQuestion.id ? normalizedNextQuestion : question,
      ),
    ),
  };
};

export const replaceFeedbackFlowSectionQuestions = (
  feedbackFlow: FeedbackFlow,
  section: FeedbackFlowQuestionSection,
  nextQuestions: FeedbackFlowQuestion[],
): FeedbackFlow => ({
  ...feedbackFlow,
  questions: sortFeedbackFlowQuestions([
    ...feedbackFlow.questions.filter((question) => question.section !== section),
    ...nextQuestions.map(normalizeFeedbackFlowQuestion),
  ]),
});

export const reorderFeedbackFlowSectionQuestions = (
  feedbackFlow: FeedbackFlow,
  section: FeedbackFlowQuestionSection,
  questionIds: string[],
): FeedbackFlow => {
  const sectionQuestions = getQuestionsBySection(feedbackFlow, section);

  if (
    sectionQuestions.length !== questionIds.length ||
    sectionQuestions.some((question) => !questionIds.includes(question.id))
  ) {
    return feedbackFlow;
  }

  const questionById = new Map(
    sectionQuestions.map((question) => [question.id, question] as const),
  );
  const reorderedQuestions = questionIds
    .map((questionId, index) => {
      const question = questionById.get(questionId);
      if (!question) {
        return null;
      }

      return normalizeFeedbackFlowQuestion({
        ...question,
        sortOrder: index,
      });
    })
    .filter((question): question is FeedbackFlowQuestion => Boolean(question));

  if (reorderedQuestions.length !== sectionQuestions.length) {
    return feedbackFlow;
  }

  return replaceFeedbackFlowSectionQuestions(
    feedbackFlow,
    section,
    reorderedQuestions,
  );
};

export const removeFeedbackFlowQuestion = (
  feedbackFlow: FeedbackFlow,
  questionId: string,
): FeedbackFlow => ({
  ...feedbackFlow,
  questions: feedbackFlow.questions.filter((question) => question.id !== questionId),
});

export const replaceFeedbackFlowQuestionId = (
  feedbackFlow: FeedbackFlow,
  previousQuestionId: string,
  nextQuestion: FeedbackFlowQuestion,
): FeedbackFlow => ({
  ...feedbackFlow,
  questions: sortFeedbackFlowQuestions(
    feedbackFlow.questions.map((question) =>
      question.id === previousQuestionId
        ? normalizeFeedbackFlowQuestion(nextQuestion)
        : question,
    ),
  ),
});

export const normalizeFeedbackFlowTermsForRequest = (
  terms?: string | null,
): string | null => {
  const normalizedTerms = terms?.trim();
  return normalizedTerms ? normalizedTerms : null;
};

export const normalizeFeedbackFlowAnswer = (
  answer: FeedbackFlowQuestionAnswer | undefined,
): FeedbackFlowQuestionAnswer | undefined => {
  if (Array.isArray(answer)) {
    return answer.map((value) => value.trim()).filter(Boolean);
  }

  return answer?.trim() ? answer.trim() : undefined;
};
